import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EmpSidebar from './EmpSidebar';
import EmpHeader from './EmpHeader';

interface EmpLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  showBackButton?: boolean;
}

const EmpLayout: React.FC<EmpLayoutProps> = ({
  children,
  title,
  subtitle,
  headerActions,
  showBackButton = false,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <div className="emp-layout min-h-screen" style={{ backgroundColor: 'hsl(0, 0%, 97.5%)' }}>
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <EmpSidebar />
      </div>

      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ease-in-out',
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        )}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 lg:hidden transform transition-transform duration-300 ease-in-out will-change-transform',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <EmpSidebar isMobile onCloseMobile={() => setMobileMenuOpen(false)} />
      </div>

      {/* Main content */}
      <main className={cn('transition-[margin-left] duration-300 h-screen flex flex-col min-w-0', 'ml-0 lg:ml-64')}>
        <EmpHeader
          title={title}
          subtitle={subtitle}
          headerActions={headerActions}
          onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        />

        {showBackButton && (
          <div className="lg:hidden px-4 pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="min-h-[44px] -ml-2 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span>Voltar</span>
            </Button>
          </div>
        )}

        <div className="p-4 sm:p-5 md:p-6 flex-1 min-h-0 min-w-0 overflow-auto">{children}</div>
      </main>
    </div>
  );
};

export default EmpLayout;
